import { Link } from "react-router-dom";
import { Calendar, MapPin, Phone, Mail, ExternalLink, ArrowRight, Facebook, Users, CheckCircle } from "lucide-react";
import SeoHead from "../components/SeoHead";
import CTABand from "../components/CTABand";
import { PageHeader } from "./Services";
import { SITE } from "../lib/site";

const SALON = {
  name: "Salon Médoc Immo 2027",
  longName: "Salon de l'Immobilier du Médoc 2027",
  date: "Samedi 20 mars 2027",
  isoStart: "2027-03-20T09:00:00+01:00",
  isoEnd: "2027-03-20T19:00:00+01:00",
  location: "Domaine de Cordet",
  locationCity: "Arsac",
  locationPostalCode: "33460",
  locationRegion: "Gironde",
  organizerPhone: "06 45 23 61 23",
  organizerPhoneRaw: "+33645236123",
  organizerEmail: "elodie.talavera@chasseur2renta.fr",
  facebookUrl: "https://www.facebook.com/profile.php?id=61594551556916",
  facebookReelUrl: "https://www.facebook.com/reel/1550832443726821/",
  facebookReelEmbed:
    "https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F1550832443726821%2F&show_text=true&width=265&t=0",
  logo: "https://customer-assets-jt897jd0.emergentagent.net/job_wonderful-greider-8/artifacts/bc893f27f3221aa2_828810836_122108865981485051_6758297087905223708_n.webp",
  banner: "https://customer-assets-jt897jd0.emergentagent.net/job_wonderful-greider-8/artifacts/034d2d2a91db931a_825286264_122108897289485051_1079693644497352014_n.jpg",
};

export default function SalonMedocImmo() {
  const eventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: SALON.longName,
    alternateName: SALON.name,
    startDate: SALON.isoStart,
    endDate: SALON.isoEnd,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    description:
      "Salon de l'Immobilier du Médoc 2027 au Domaine de Cordet à Arsac. Termite Habitat 33 y présentera ses solutions de traitement et de prévention anti-termites, bois, mérule et humidité pour tous les projets immobiliers du Médoc.",
    image: [SALON.logo, SALON.banner],
    location: {
      "@type": "Place",
      name: SALON.location,
      address: {
        "@type": "PostalAddress",
        streetAddress: "Domaine de Cordet",
        addressLocality: SALON.locationCity,
        postalCode: SALON.locationPostalCode,
        addressRegion: SALON.locationRegion,
        addressCountry: "FR",
      },
    },
    organizer: {
      "@type": "Organization",
      name: "Salon Médoc Immo",
      url: SALON.facebookUrl,
      telephone: SALON.organizerPhoneRaw,
      email: SALON.organizerEmail,
    },
    performer: {
      "@type": "Organization",
      name: "Termite Habitat 33",
      url: "https://termitebordeaux.fr",
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: "https://termitebordeaux.fr/salon-medoc-immo-2027",
      validFrom: "2026-05-23",
    },
    inLanguage: "fr-FR",
  };

  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: "Salon Médoc Immo 2027 — Teaser officiel Facebook",
    description:
      "Vidéo officielle de présentation du Salon de l'Immobilier du Médoc 2027 au Domaine de Cordet à Arsac. Rencontrez Termite Habitat 33 sur le stand le samedi 20 mars 2027.",
    thumbnailUrl: SALON.banner,
    uploadDate: "2026-07-08",
    contentUrl: SALON.facebookReelUrl,
    embedUrl: SALON.facebookReelEmbed,
    publisher: {
      "@type": "Organization",
      name: "Salon Médoc Immo",
      url: SALON.facebookUrl,
    },
  };

  return (
    <div data-testid="salon-page" className="bg-[#FFF9F2]">
      <SeoHead
        title="Salon Médoc Immo 2027 — Termite Habitat 33 à Arsac le 20 mars"
        description="Rencontrez Termite Habitat 33 au Salon de l'Immobilier du Médoc 2027, samedi 20 mars 2027 au Domaine de Cordet à Arsac. Conseils termites, prévention, traitement, échanges experts pour tous vos projets immobiliers girondins."
        canonical={SITE.url + "/salon-medoc-immo-2027"}
        image={SALON.banner}
        keywords={[
          "Salon Médoc Immo 2027",
          "Salon de l'Immobilier du Médoc",
          "Salon immobilier Arsac",
          "Domaine de Cordet Arsac",
          "Termite Habitat 33 salon",
          "salon immobilier Gironde 2027",
          "salon immo Médoc mars 2027",
          "termites Médoc",
          "exposant salon immo Arsac",
          "prévention termites Médoc salon",
        ]}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Salon Médoc Immo 2027", url: SITE.url + "/salon-medoc-immo-2027" },
        ]}
        schema={[eventSchema, videoSchema]}
      />

      {/* HERO */}
      <section className="container-x py-14 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#7A2E39] text-white text-[11px] uppercase tracking-widest rounded-full mb-5">
              <Calendar className="w-3.5 h-3.5" /> Événement à venir
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-gray-900 leading-[1.05] mb-5">
              Termite Habitat 33 au{" "}
              <span className="text-[#7A2E39]">Salon Médoc Immo 2027</span>.
            </h1>
            <p className="text-lg text-gray-700 mb-7">
              Samedi <strong>20 mars 2027</strong>, au Domaine de Cordet à <strong>Arsac (33)</strong>,
              retrouvez notre cabinet lors du Salon de l'Immobilier du Médoc. Conseils termites,
              prévention, solutions de traitement : nous répondons à toutes vos questions sur site.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              <a
                href={SALON.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="salon-fb-link-hero"
              >
                <Facebook className="w-4 h-4" /> Suivre le salon sur Facebook
              </a>
              <a href={`tel:${SITE.phoneRaw}`} className="btn-ghost" data-testid="salon-call-hero">
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
            </div>
            <div className="flex items-center gap-5 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#7A2E39]" /> 20 mars 2027
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#7A2E39]" /> Arsac (33)
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[#7A2E39]" /> Entrée libre
              </div>
            </div>
          </div>
          <div className="relative" data-testid="salon-logo-block">
            <div className="aspect-square max-w-md mx-auto bg-white border border-[#D5CABD] rounded-sm overflow-hidden shadow-lg">
              <img src={SALON.logo} alt="Logo officiel Salon Médoc Immo 2027" loading="eager" className="w-full h-full object-contain p-10" />
            </div>
          </div>
        </div>
      </section>

      {/* BANNER */}
      <section className="container-x pb-10">
        <figure className="bg-white border border-[#D5CABD] rounded-sm overflow-hidden">
          <img
            src={SALON.banner}
            alt="Bannière officielle du Salon de l'Immobilier du Médoc 2027 — Rencontrez les pros, solutions concrètes"
            loading="lazy"
            className="w-full h-auto object-cover"
            data-testid="salon-banner"
          />
          <figcaption className="p-4 text-xs text-gray-600 italic text-center">
            Bannière officielle du Salon Médoc Immo 2027 — Rencontrez les pros, solutions concrètes.
          </figcaption>
        </figure>
      </section>

      {/* VIDEO FACEBOOK — Reel officiel du salon */}
      <section className="container-x py-14" data-testid="salon-video">
        <div className="grid lg:grid-cols-5 gap-10 items-center max-w-6xl mx-auto">
          <div className="lg:col-span-2 flex justify-center">
            <div className="bg-white border border-[#D5CABD] rounded-sm overflow-hidden shadow-md">
              <iframe
                src={SALON.facebookReelEmbed}
                width="265"
                height="591"
                style={{ border: "none", overflow: "hidden" }}
                scrolling="no"
                frameBorder="0"
                allowFullScreen
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                title="Vidéo officielle du Salon Médoc Immo 2027 — Facebook Reel"
                data-testid="salon-video-iframe"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="eyebrow mb-3 text-[#7A2E39]">Vidéo officielle</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-gray-900 mb-4 leading-tight">
              Découvrez le teaser du Salon Médoc Immo 2027.
            </h2>
            <p className="text-gray-700 mb-5">
              La vidéo officielle publiée sur la page Facebook du Salon Médoc Immo présente les
              organisateurs et donne un aperçu de l'ambiance qui vous attend le 20 mars 2027 au
              Domaine de Cordet à Arsac. Rencontrez les professionnels, échangez sur vos projets
              immobiliers et repartez avec des solutions concrètes.
            </p>
            <p className="text-gray-700 mb-5">
              Termite Habitat 33 y sera présent toute la journée pour conseiller propriétaires,
              acquéreurs et professionnels sur la prévention et le traitement des termites en
              Gironde.
            </p>
            <a
              href={SALON.facebookReelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline text-sm font-bold uppercase tracking-widest"
              data-testid="salon-video-fb-link"
            >
              Voir la vidéo sur Facebook →
            </a>
          </div>
        </div>
      </section>

      {/* INFOS PRATIQUES */}
      <section className="container-x py-14 bg-[#E8DCC9]/40" data-testid="salon-infos">
        <h2 className="font-serif text-3xl text-gray-900 mb-8 text-center">
          Informations pratiques
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
          <div className="bg-white border border-[#D5CABD] p-6 rounded-sm">
            <Calendar className="w-6 h-6 text-[#7A2E39] mb-3" />
            <div className="eyebrow mb-1">Date</div>
            <div className="font-serif text-xl text-gray-900">Samedi 20 mars 2027</div>
          </div>
          <div className="bg-white border border-[#D5CABD] p-6 rounded-sm">
            <MapPin className="w-6 h-6 text-[#7A2E39] mb-3" />
            <div className="eyebrow mb-1">Lieu</div>
            <div className="font-serif text-xl text-gray-900">Domaine de Cordet</div>
            <div className="text-sm text-gray-700 mt-1">33460 Arsac — Gironde</div>
          </div>
          <div className="bg-white border border-[#D5CABD] p-6 rounded-sm">
            <Phone className="w-6 h-6 text-[#7A2E39] mb-3" />
            <div className="eyebrow mb-1">Contact organisateur</div>
            <a href={`tel:${SALON.organizerPhoneRaw}`} className="font-serif text-xl text-gray-900 hover:text-[#7A2E39]" data-testid="salon-organizer-phone">
              {SALON.organizerPhone}
            </a>
          </div>
          <div className="bg-white border border-[#D5CABD] p-6 rounded-sm">
            <Mail className="w-6 h-6 text-[#7A2E39] mb-3" />
            <div className="eyebrow mb-1">Email organisateur</div>
            <a href={`mailto:${SALON.organizerEmail}`} className="font-serif text-sm text-gray-900 hover:text-[#7A2E39] break-all" data-testid="salon-organizer-email">
              {SALON.organizerEmail}
            </a>
          </div>
        </div>
      </section>

      {/* POURQUOI VENIR */}
      <section className="container-x py-14" data-testid="salon-why">
        <div className="max-w-4xl mx-auto">
          <div className="eyebrow mb-3">Pourquoi venir nous rencontrer&nbsp;?</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-gray-900 mb-8">
            Un projet immobilier dans le Médoc ? Nous sommes là pour vous.
          </h2>

          <div className="space-y-8">
            <article>
              <h3 className="font-serif text-2xl text-[#7A2E39] mb-3">
                Vous achetez une maison dans le Médoc
              </h3>
              <p className="text-gray-800 leading-relaxed">
                Avant tout achat immobilier en Gironde, la question des termites est capitale.
                Le Médoc est classé zone contaminée par les termites et toute vente déclenche
                un état parasitaire obligatoire. Venez échanger avec nos experts pour comprendre
                les signes à repérer, les coûts de traitement et les garanties à exiger du vendeur.
              </p>
            </article>

            <article>
              <h3 className="font-serif text-2xl text-[#7A2E39] mb-3">
                Vous êtes propriétaire dans le Médoc
              </h3>
              <p className="text-gray-800 leading-relaxed">
                La surveillance régulière de votre maison médocaine est le meilleur rempart.
                Nous présenterons sur le salon nos protocoles de prévention : barrière chimique
                périphérique, stations d'appâts Termigard System en périmètre, et contrat de
                surveillance annuel pour les bâtis anciens antérieurs à 1990.
              </p>
            </article>

            <article>
              <h3 className="font-serif text-2xl text-[#7A2E39] mb-3">
                Vous êtes professionnel de l'immobilier
              </h3>
              <p className="text-gray-800 leading-relaxed">
                Notaires, agents immobiliers, chasseurs d'appartement, syndics : vos clients vous
                posent des questions sur les termites à chaque transaction. Rencontrez
                Termite Habitat 33 au salon pour approfondir vos connaissances techniques et
                tisser un partenariat local avec un applicateur certifié CTB-P+.
              </p>
            </article>

            <article>
              <h3 className="font-serif text-2xl text-[#7A2E39] mb-3">
                Vous suspectez une infestation
              </h3>
              <p className="text-gray-800 leading-relaxed">
                Essaimage de termites ailés, cordonnets de terre sur mur, bois creux au sondage,
                sciure fine sous les poutres : apportez-nous vos photos et vos interrogations.
                Nous vous conseillons gratuitement sur place et planifions, si vous le souhaitez,
                une visite d'expertise à domicile.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* CE QU'ON PRÉSENTE */}
      <section className="container-x py-14 bg-[#1B1814] text-white" data-testid="salon-services">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl mb-8 text-center">
            Ce que Termite Habitat 33 présentera sur le stand
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              ["Traitement curatif par injection à cœur", "Protocole CTB-P+ pour charpentes et bois structurels infestés."],
              ["Pièges-appâts Termigard System", "Stations enterrées en périmètre + Wall Box intérieurs."],
              ["Barrière chimique périphérique", "Protection préventive en pied de mur contre les remontées."],
              ["Traitement mérule (Serpula lacrymans)", "Protocole fongicide curatif et assainissement."],
              ["Traitement humidité & caves médocaines", "Remontées capillaires, infiltrations, ventilation."],
              ["Garantie 5 ans retraitement gratuit", "Couverture écrite incluse dans chaque devis."],
            ].map(([title, desc]) => (
              <div key={title} className="flex gap-3 p-4 bg-white/5 rounded-sm">
                <CheckCircle className="w-5 h-5 text-[#D5A66A] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold mb-1">{title}</div>
                  <div className="text-sm text-white/70">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FACEBOOK CTA */}
      <section className="container-x py-14 max-w-3xl text-center">
        <Facebook className="w-10 h-10 text-[#1877F2] mx-auto mb-4" />
        <h2 className="font-serif text-3xl text-gray-900 mb-3">
          Suivez l'actualité du salon sur Facebook
        </h2>
        <p className="text-gray-700 mb-6">
          Programme, exposants, horaires, animations : la page officielle du Salon Médoc Immo
          publie toutes les mises à jour avant l'événement.
        </p>
        <a
          href={SALON.facebookUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
          data-testid="salon-fb-link-footer"
        >
          <Facebook className="w-4 h-4" /> Page Facebook officielle <ExternalLink className="w-4 h-4" />
        </a>
      </section>

      {/* LIEN BLOG + ZONES */}
      <section className="container-x py-10 bg-[#E8DCC9]/40">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="font-serif text-2xl text-gray-900 mb-3">En attendant le salon</h3>
          <p className="text-gray-700 mb-5">
            Découvrez notre article dédié avec le programme détaillé et toutes nos interventions
            dans le Médoc.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/blog/salon-medoc-immo-2027-termite-habitat-33" className="btn-ghost" data-testid="salon-blog-link">
              Lire l'article dédié <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/zones" className="btn-ghost" data-testid="salon-zones-link">
              Nos zones Médoc & Gironde <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
